import { useState, useCallback, useRef, useEffect } from 'react';
import { loadStripeTerminal } from '@stripe/terminal-js';
import { paymentService } from '../../../_services/payment.service';
import siteSetting from '../../../config/env';

/**
 * Hook for Stripe Terminal card reader operations.
 * Handles discovery, connection, payment collection, and processing.
 * No sensitive data (secrets, tokens) is stored in state or logged.
 */
export default function useStripeTerminal() {
  const terminalRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [connectedReader, setConnectedReader] = useState(null);
  const [error, setError] = useState(null);
  const [tipAmount, setTipAmount] = useState(0);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (terminalRef.current) {
        try { terminalRef.current.disconnectReader(); } catch (e) { /* ignore */ }
      }
    };
  }, []);

  // Fetch connection token from backend (never expose the secret)
  const fetchConnectionToken = useCallback(async () => {
    const res = await paymentService.fetchConnectionToken();
    return res.data.responseData.result.secret;
  }, []);

  // Initialize terminal if needed
  const ensureTerminal = useCallback(async () => {
    if (terminalRef.current) return terminalRef.current;

    const StripeTerminal = await loadStripeTerminal();
    const t = StripeTerminal.create({
      onFetchConnectionToken: fetchConnectionToken,
      onUnexpectedReaderDisconnect: () => {
        setIsConnected(false);
        setConnectedReader(null);
      },
    });
    terminalRef.current = t;
    return t;
  }, [fetchConnectionToken]);

  // Discover and connect to a specific reader
  const connectReader = useCallback(async (reader, locationId) => {
    setIsConnecting(true);
    setError(null);

    try {
      const t = await ensureTerminal();

      // Discover readers
      const config = {
        simulated: siteSetting.simulated,
        location: locationId,
      };

      const discoverResult = await t.discoverReaders(config);

      if (discoverResult.error) {
        setError(discoverResult.error.message);
        setIsConnecting(false);
        return false;
      }

      if (discoverResult.discoveredReaders.length === 0) {
        setError('No card readers found');
        setIsConnecting(false);
        return false;
      }

      // Find the selected reader or use first available
      const targetReader = reader?.serial_number
        ? discoverResult.discoveredReaders.find(r => r.serial_number === reader.serial_number)
        : discoverResult.discoveredReaders[0];

      if (!targetReader) {
        setError('Selected reader not found');
        setIsConnecting(false);
        return false;
      }

      // Disconnect existing reader first
      try { await t.disconnectReader(); } catch (e) { /* ignore */ }

      const connectResult = await t.connectReader(targetReader);

      if (connectResult.error) {
        setError(connectResult.error.message);
        setIsConnecting(false);
        return false;
      }

      setIsConnected(true);
      setConnectedReader(connectResult.reader);
      setIsConnecting(false);
      return true;

    } catch (e) {
      setError('Failed to connect to card reader');
      setIsConnecting(false);
      return false;
    }
  }, [ensureTerminal]);

  // Process a payment through the connected reader
  // onCapture: optional async callback for capture (e.g. Redux dispatch). Falls back to direct service call.
  const processPayment = useCallback(async ({ clientSecret, paymentIntentId, bookingId, servicesToBook, voucherIds, onCapture, isDeposit }) => {
    if (!terminalRef.current || !isConnected) {
      setError('Card reader not connected');
      return null;
    }

    setIsProcessing(true);
    setError(null);
    setTipAmount(0);

    try {
      const t = terminalRef.current;

      // Collect payment method from the reader
      const collectResult = await t.collectPaymentMethod(clientSecret);

      if (collectResult.error) {
        setError(collectResult.error.message);
        setIsProcessing(false);
        return null;
      }

      // Check for tip from the terminal
      const terminalTip = collectResult.paymentIntent?.amount_details?.tip?.amount;
      if (terminalTip) {
        setTipAmount(terminalTip / 100);
      }

      // Process the payment on the terminal
      const processResult = await t.processPayment(collectResult.paymentIntent);

      if (processResult.error) {
        setError(processResult.error.message);
        setIsProcessing(false);
        return null;
      }

      // Capture on backend (use onCapture callback if provided, else direct service call)
      const captureParams = {
        paymentIntentId: processResult.paymentIntent.id,
        bookingId,
        servicesToBook,
        voucherIds: voucherIds || [],
        selectedPaymentType: 5,
      };
      if (isDeposit) captureParams.isDeposit = 1;
      const captureResponse = onCapture
        ? await onCapture(captureParams)
        : await paymentService.paymentIntentsCapture(captureParams);

      setIsProcessing(false);

      if (captureResponse?.data?.statusCode === 1) {
        const capturedTip = captureResponse.data?.responseData?.result?.tipAmount || 0;
        if (capturedTip > 0) setTipAmount(capturedTip);

        const capturedAmount = (captureResponse.data?.responseData?.result?.amount || 0) / 100;
        return { success: true, amount: capturedAmount, tipAmount: capturedTip };
      } else {
        setError('Payment capture failed. Please try again.');
        return null;
      }

    } catch (e) {
      setError('Payment processing failed. Please try again.');
      setIsProcessing(false);
      return null;
    }
  }, [isConnected]);

  // Disconnect reader
  const disconnect = useCallback(async () => {
    if (terminalRef.current) {
      try { await terminalRef.current.disconnectReader(); } catch (e) { /* ignore */ }
    }
    setIsConnected(false);
    setConnectedReader(null);
  }, []);

  return {
    isConnected,
    isConnecting,
    isProcessing,
    connectedReader,
    error,
    tipAmount,
    connectReader,
    processPayment,
    disconnect,
    clearError: () => setError(null),
  };
}
